import { formatTagFilter, METRIC_AGGREGATES, type MetricAggregate } from '@vision360/contracts';
import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { tagLabel } from './format';
import { TIME_RANGES, type TimeRange, useMetricSeriesList, useSources } from './hooks';

/**
 * Editor building blocks (TechSpec §9.1.1). Native controls: keyboard-first
 * for free (rule 6). Every field is labelled; widget editors compose these.
 */

const selectClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 aria-[invalid=true]:border-destructive';

function Field({
  label,
  id,
  error,
  children,
}: {
  label: string;
  id: string;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {error && (
        <p id={`${id}-error`} className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

/** Ties a control to its error message for assistive tech. */
const invalid = (id: string, error: string | undefined) =>
  error ? { 'aria-invalid': true, 'aria-describedby': `${id}-error` } : {};

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  error,
}: {
  label: string;
  value: string | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string | undefined;
}) {
  const id = useId();
  return (
    <Field label={label} id={id} error={error}>
      <Input
        id={id}
        {...invalid(id, error)}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </Field>
  );
}

export function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step,
  error,
}: {
  label: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
  min?: number;
  max?: number;
  step?: number;
  error?: string | undefined;
}) {
  const id = useId();
  return (
    <Field label={label} id={id} error={error}>
      <Input
        id={id}
        {...invalid(id, error)}
        type="number"
        inputMode="decimal"
        value={value ?? ''}
        min={min}
        max={max}
        step={step ?? 'any'}
        onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
      />
    </Field>
  );
}

export function SelectField<T extends string>({
  label,
  value,
  options,
  onChange,
  placeholder,
  error,
}: {
  label: string;
  value: T | undefined;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
  placeholder?: string | undefined;
  error?: string | undefined;
}) {
  const id = useId();
  return (
    <Field label={label} id={id} error={error}>
      <select
        id={id}
        {...invalid(id, error)}
        className={selectClass}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value as T)}
      >
        {placeholder !== undefined && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

export function CheckboxField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-center gap-2">
      <input
        id={id}
        type="checkbox"
        className="h-4 w-4 accent-primary"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <Label htmlFor={id}>{label}</Label>
    </div>
  );
}

/** Any source (§9.2 rule 3: no hard-coded source/widget pairing). */
export function SourceField({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (sourceId: string) => void;
}) {
  const { t } = useTranslation('widgets');
  const sources = useSources();
  return (
    <SelectField
      label={t('fields.source')}
      value={value}
      placeholder={t('fields.sourcePlaceholder')}
      options={(sources.data?.sources ?? []).map((s) => ({ value: s.id, label: s.name }))}
      onChange={onChange}
    />
  );
}

/**
 * Metric name, then which series of it. `tags` = {} means every series.
 * Choosing a new metric resets the series.
 */
export function MetricFields({
  sourceId,
  name,
  tags,
  onChange,
  allowAllSeries = true,
}: {
  sourceId: string | undefined;
  name: string | undefined;
  tags: Record<string, string> | undefined;
  onChange: (next: { name: string; tags: Record<string, string> }) => void;
  allowAllSeries?: boolean;
}) {
  const { t } = useTranslation('widgets');
  const list = useMetricSeriesList(sourceId);
  const series = list.data?.series ?? [];
  const tagSets = series.find((s) => s.name === name)?.tagSets ?? [];
  const current = formatTagFilter(tags);

  if (sourceId && list.isSuccess && series.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('fields.metricNone')}</p>;
  }

  const tagOptions = [
    ...(allowAllSeries ? [{ value: '', label: t('fields.tagsAll') }] : []),
    ...tagSets
      .filter((ts) => Object.keys(ts).length > 0)
      .map((ts) => ({ value: formatTagFilter(ts), label: tagLabel(ts) })),
  ];

  return (
    <>
      <SelectField
        label={t('fields.metric')}
        value={name}
        placeholder={t('fields.metricPlaceholder')}
        options={series.map((s) => ({ value: s.name, label: s.name }))}
        onChange={(next) => {
          const sets = series.find((s) => s.name === next)?.tagSets ?? [];
          // "All series" when allowed; otherwise the first one, changeable below.
          const only = allowAllSeries ? {} : (sets[0] ?? {});
          onChange({ name: next, tags: only });
        }}
      />
      {tagOptions.length > 1 && (
        <SelectField
          label={t('fields.tags')}
          value={current}
          options={tagOptions}
          onChange={(value) =>
            onChange({
              name: name ?? '',
              tags:
                tagSets.find((ts) => formatTagFilter(ts) === value) ??
                ({} as Record<string, string>),
            })
          }
        />
      )}
    </>
  );
}

export function RangeField({
  value,
  onChange,
}: {
  value: TimeRange | undefined;
  onChange: (range: TimeRange) => void;
}) {
  const { t } = useTranslation('widgets');
  return (
    <SelectField
      label={t('fields.range')}
      value={value}
      options={TIME_RANGES.map((r) => ({ value: r, label: t(`fields.ranges.${r}`) }))}
      onChange={onChange}
    />
  );
}

export function AggField({
  value,
  onChange,
}: {
  value: MetricAggregate | undefined;
  onChange: (agg: MetricAggregate) => void;
}) {
  const { t } = useTranslation('widgets');
  return (
    <SelectField
      label={t('fields.agg')}
      value={value}
      options={METRIC_AGGREGATES.map((a) => ({ value: a, label: t(`fields.aggs.${a}`) }))}
      onChange={onChange}
    />
  );
}

/** Units the formatter understands; bytes scale automatically. */
export const UNITS = ['', '%', '°C', 'ms', 's', 'B', 'B/s', '€'] as const;

export function UnitField({
  value,
  onChange,
}: {
  value: string | undefined;
  onChange: (unit: string | undefined) => void;
}) {
  const { t } = useTranslation('widgets');
  return (
    <SelectField
      label={t('fields.unit')}
      value={value ?? ''}
      options={UNITS.map((u) => ({ value: u, label: u === '' ? t('fields.unitNone') : u }))}
      onChange={(u) => onChange(u === '' ? undefined : u)}
    />
  );
}
